/**
 * supplierFeeHelpers.ts
 * 협력사 수수료 관리 관련 유틸리티 함수 모음
 */

import { formatDateToDisplay, formatDateTimeToDisplay } from "@/shared/utils/formatter";
import {
    SupplierFee,
    SupplierFeeDto,
    Rider,
    RiderDto
} from "@/features/partner/supplierFee/riderToPartner/types/supplierFee.types";

// 공통 상수 re-export (shared/constants에서 관리)
export { COMMISSION_TYPE_OPTIONS, getAmountUnit } from '../../shared/constants';

// 라이더 상태 필터 옵션
export const STATUS_FILTER_OPTIONS = [
    { value: '', label: '전체' },
    { value: 'UNDER_CONTRACT', label: '계약 중' },
    { value: 'TERMINATED_CONTRACT', label: '계약 종료' },
] as const;

// 페이지네이션 상수
export const ITEMS_PER_PAGE = {
    DEFAULT: 10,
    LARGE: 15,
    EXTRA_LARGE: 20,
} as const;

/**
 * 필터 스토리지 키
 */
export const FILTER_STORAGE_KEY = 'supplierFeeFilters';

/**
 * 계약 중 여부 확인
 * @param contStatus 계약 상태
 * @returns 계약 중 여부
 */
export const isUnderContract = (contStatus: string): boolean => {
    return contStatus === 'UNDER_CONTRACT';
};

/**
 * 수수료 저장 유효성 검사
 * @param commissionType 수수료 구분
 * @param amount 금액
 * @returns 유효성 검사 결과 객체
 */
export const validateFeeData = (
    commissionType: string,
    amount: string
): { valid: boolean; message: string } => {
    if (!commissionType) {
        return { valid: false, message: '수수료구분을 선택해주세요.' };
    }
    if (!amount) {
        return { valid: false, message: '금액을 입력해주세요.' };
    }
    return { valid: true, message: '' };
};

export type { RiderCommissionRow } from '../types/supplierFee.types';
export type { HistoryModalItem as CommissionHistoryItem } from '../../shared/components/HistoryModal';

/**
 * DTO -> Domain 변환 (supplierFee.types.ts에서 이전)
 */
export const supplierFeeFromDto = (dto: SupplierFeeDto, riderName?: string): SupplierFee => {
    let formattedUpdDate = '';
    if (dto.updDate) {
        if (Array.isArray(dto.updDate)) {
            const [year, month, day, hour, minute] = dto.updDate;
            formattedUpdDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')} ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        } else if (typeof dto.updDate === 'string') {
            formattedUpdDate = formatDateTimeToDisplay(dto.updDate);
        }
    }

    return {
        id: dto.seqNo,
        feeCode: dto.feeCode,
        feeName: dto.feeName,
        target: dto.target,
        targetId: dto.riderId,
        targetName: dto.target === 'A' ? '전체' : (riderName || dto.riderId || ''),
        feeType: dto.calcType === '1' ? '정액' : '정률',
        amount: dto.calcType === '1'
            ? (dto.feeAmt?.toString() || '0')
            : (dto.feeRate?.toString() || '0'),
        startDate: formatDateToDisplay(dto.startDay),
        endDate: formatDateToDisplay(dto.endDay),
        registeredDate: formatDateToDisplay(dto.insDate),
        updatedDateTime: formattedUpdDate
    };
};

/**
 * 라이더 DTO -> Domain 변환 (supplierFee.types.ts에서 이전)
 */
export const riderFromDto = (dto: RiderDto): Rider => ({
    id: dto.riderId,
    name: dto.riderName,
    supplierId: dto.suppId
});
